import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowRight, MapPin } from "lucide-react";

import { listQuery } from "@/lib/queries";
import { PageHero, Section, EmptyState } from "@/components/site/bits";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/trabalhos")({
  head: () => ({
    meta: [
      { title: "Trabalhos Realizados | Professor Rurdiney — 40123" },
      {
        name: "description",
        content: "Ações e conquistas concretas do Professor Rurdiney pelas comunidades de São Luís.",
      },
      { property: "og:title", content: "Trabalhos Realizados | Professor Rurdiney — 40123" },
      {
        property: "og:description",
        content: "Quem faz, prova. Veja os trabalhos realizados pelo Professor Rurdiney.",
      },
    ],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(listQuery("works")),
      context.queryClient.ensureQueryData(listQuery("work_categories")),
    ]),
  component: TrabalhosPage,
});

function TrabalhosPage() {
  const { data: works } = useSuspenseQuery(listQuery("works"));
  const { data: categories } = useSuspenseQuery(listQuery("work_categories"));
  const [active, setActive] = useState<string | null>(null);

  const filtered = active ? works.filter((w: any) => w.category_id === active) : works;

  return (
    <>
      <PageHero
        eyebrow="Realizações"
        title="Trabalhos realizados"
        description="Quem faz, prova. Conheça as ações concretas que já mudaram a realidade de bairros e comunidades."
      />

      <Section>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActive(null)}
            className={cn(
              "rounded-full border px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
              active === null
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary",
            )}
          >
            Todos
          </button>
          {categories.map((c: any) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={cn(
                "rounded-full border px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
                active === c.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary hover:text-primary",
              )}
            >
              {c.name}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10">
            <EmptyState message="Nenhum trabalho encontrado para este filtro." />
          </div>
        ) : (
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {filtered.map((w: any) => (
              <Link
                key={w.id}
                to="/trabalhos/$slug"
                params={{ slug: w.slug }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:shadow-glow"
              >
                <div className="flex items-center justify-between gap-3 border-b border-border p-5">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    <MapPin className="size-4 text-accent-deep" /> {w.location}
                  </span>
                  {w.date && (
                    <span className="text-xs text-muted-foreground">{formatDate(w.date)}</span>
                  )}
                </div>
                <div className="flex-1 p-5">
                  <h3 className="text-lg text-foreground">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.summary}</p>
                </div>
                <div className="border-t border-border px-5 py-3">
                  <span className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-primary group-hover:text-accent-deep">
                    Ver detalhes <ArrowRight className="size-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}
